import { createContext, useContext } from "react";
import { useParams } from "react-router-dom";

import { getTemplateConfig } from "../config/ecommerceTemplateConfig";

const TemplateContext = createContext(null);

/**
 * Provides the active ecommerce-template config + URL `basePath` to every
 * descendant. `basePath` is the route prefix (e.g. `/template/coffee` for the
 * showroom or `/site/<biz-slug>` for a real published store) — child links use
 * it like `<Link to={`${basePath}/shop`}>` so the same components work
 * regardless of which mount they're under.
 *
 * Args (all optional):
 *  - industryKey  one of SUPPORTED_INDUSTRIES; used to look up a preset.
 *                 When mounted under `/template/:industryKey` we auto-read
 *                 from URL params, but callers (like the real ecommerce shell)
 *                 can pass `null` and supply everything via `overrides`.
 *  - overrides    deep partial that wins over preset + default
 *  - basePath     explicit URL prefix; falls back to `/template/<industryKey>`
 *  - slug         business slug (when rendering a real published store) — used
 *                 by API helpers to hit `/api/public/site/<slug>/products`
 *                 instead of the generic `/api/products` route.
 *  - checkoutEnabled  true only when the business has finished Stripe Connect
 *                 onboarding. Lets Checkout switch from the localStorage demo
 *                 path to a real Stripe-hosted multi-line cart checkout.
 */
export function TemplateProvider({ industryKey: explicitKey, overrides, basePath: explicitBase, slug, checkoutEnabled, websiteContent = null, summary = null, children }) {
  const params = useParams();
  const industryKey = explicitKey === undefined ? (params.industryKey ?? null) : explicitKey;
  const config = getTemplateConfig(industryKey, overrides);
  const basePath = explicitBase ?? (industryKey ? `/template/${industryKey}` : "");

  return (
    <TemplateContext.Provider value={{
      industryKey,
      config,
      basePath,
      slug: slug ?? null,
      checkoutEnabled: !!checkoutEnabled,
      websiteContent,
      summary,
    }}>
      {children}
    </TemplateContext.Provider>
  );
}

export function useTemplate() {
  const ctx = useContext(TemplateContext);
  if (!ctx) {
    // Soft fallback so callers outside a provider still get sane defaults
    // (useful for storybook / one-off renders).
    return { industryKey: null, config: getTemplateConfig(null), basePath: "", slug: null, checkoutEnabled: false, websiteContent: null, summary: null };
  }
  return ctx;
}
